import type { TestDriverType } from "@memorang/constants/config/schema";
import type { BaseItemSchema, OriginalItemTypes } from "@memorang/types";
import { isPteAssessorAgentItem } from "../constants/pte/assessor";
import { isPteFeedbackAgentItem } from "../constants/pte/feedback";
import useAIFeedbackStore from "../hooks/useAIFeedbackStore";
import useSummativeAssessmentStore from "../hooks/useSummativeAssessmentStore";

export const shouldUseSummativeAiAssessment = (
	testDriver: TestDriverType | undefined,
	isSummativeTest: boolean | undefined,
	itemType: OriginalItemTypes | undefined,
): boolean =>
	testDriver === "pte" &&
	Boolean(isSummativeTest) &&
	isPteAssessorAgentItem(itemType);

// Item-type sets are disjoint from the assessor's, so both gates share trigger call sites without overlap.
export const shouldUseSummativeDynamicFeedback = (
	testDriver: TestDriverType | undefined,
	isSummativeTest: boolean | undefined,
	itemType: OriginalItemTypes | undefined,
): boolean =>
	testDriver === "pte" &&
	Boolean(isSummativeTest) &&
	isPteFeedbackAgentItem(itemType);

const isBlankResponse = (value: unknown): boolean => {
	if (value == null) {
		return true;
	}
	return typeof value === "string" && value.trim().length === 0;
};

// Must match the runner's no-op-if-blank rule; callers predict whether a run will grade.
export const hasCapturedResponse = (itemId: number): boolean =>
	!isBlankResponse(useAIFeedbackStore.getState().userResponse?.[itemId]);

export const getSummativeAiItemIds = (
	sessionItems: BaseItemSchema[][],
	testDriver: TestDriverType | undefined,
	isSummativeTest: boolean | undefined,
): number[] =>
	sessionItems
		.flat()
		.filter((item) =>
			shouldUseSummativeAiAssessment(
				testDriver,
				isSummativeTest,
				item.metadata?.type as OriginalItemTypes | undefined,
			),
		)
		.map((item) => item.id!);

export const getSummativeFeedbackItemIds = (
	sessionItems: BaseItemSchema[][],
	testDriver: TestDriverType | undefined,
	isSummativeTest: boolean | undefined,
): number[] =>
	sessionItems
		.flat()
		.filter((item) =>
			shouldUseSummativeDynamicFeedback(
				testDriver,
				isSummativeTest,
				item.metadata?.type as OriginalItemTypes | undefined,
			),
		)
		.map((item) => item.id!);

// Feedback items don't gate on hasCapturedResponse like the assessor: score is
// already deterministic, so the poll only needs to wait on items this app
// session actually started a feedback run for. Stranded (unpersistable resume
// re-fires) are excluded so they can't stall the poll to timeout.
const getGatedFeedbackItemIds = (
	sessionItems: BaseItemSchema[][],
	testDriver: TestDriverType | undefined,
	isSummativeTest: boolean | undefined,
): number[] => {
	const runStore = useSummativeAssessmentStore.getState();
	return getSummativeFeedbackItemIds(
		sessionItems,
		testDriver,
		isSummativeTest,
	).filter(
		(itemId) =>
			runStore.getRunState(itemId).status !== "idle" &&
			!runStore.isFeedbackStranded(itemId),
	);
};

// Skipped items stay MISSING; excluding them keeps the submit poll from stalling to timeout.
export const getGatedAiItemIds = (
	sessionItems: BaseItemSchema[][],
	testDriver: TestDriverType | undefined,
	isSummativeTest: boolean | undefined,
): number[] => [
	...getSummativeAiItemIds(sessionItems, testDriver, isSummativeTest).filter(
		hasCapturedResponse,
	),
	...getGatedFeedbackItemIds(sessionItems, testDriver, isSummativeTest),
];
